import React, { useMemo } from "react";
import { X, Save } from "lucide-react";
import TinyMCEFullEditor from "../../../components/editors/TinyMCEFullEditor";

export const ITEM_TYPES = {
  HEADING: "HEADING",
  PARAMETER: "PARAMETER",
  NOTES: "NOTES",
  RICH_TEXT: "RICH_TEXT",
  FREE_TEXT: "FREE_TEXT",
};

export const ITEM_LABELS = {
  [ITEM_TYPES.HEADING]: "Heading",
  [ITEM_TYPES.NOTES]: "Notes",
  [ITEM_TYPES.RICH_TEXT]: "Other Text",
  [ITEM_TYPES.FREE_TEXT]: "Free Text",
  [ITEM_TYPES.PARAMETER]: "Parameter",
};

export const GENDER = {
  BOTH: "Both",
  MALE: "Male",
  FEMALE: "Female",
};

const GENDER_OPTIONS = [GENDER.BOTH, GENDER.MALE, GENDER.FEMALE];

// ---------- helpers ----------
const stripHtml = (html) => {
  try {
    let s = String(html || "");
    s = s
      .replace(/<\/(td|th)>/gi, " ")
      .replace(/<\/tr>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n");

    const text = s
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    return text;
  } catch (e) {
    console.error(e);
    return "";
  }
};

const isEmptyHtml = (html) => {
  try {
    return stripHtml(html).length === 0;
  } catch (e) {
    console.error(e);
    return true;
  }
};

const GenderSelect = ({ value, onChange }) => {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Gender
      </label>
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
      >
        {GENDER_OPTIONS.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </select>
    </div>
  );
};

export default function ReportItemModal({
  open,
  onClose,
  editingItem,

  itemType,

  itemTitle,
  setItemTitle,

  itemHtml,
  setItemHtml,

  itemSortOrder,
  setItemSortOrder,

  itemGender,
  setItemGender,

  maxPosition = 1,

  onSave, // async
}) {
  const typeLabel = useMemo(
    () => ITEM_LABELS[itemType] || itemType,
    [itemType],
  );

  const safeGender = useMemo(() => {
    try {
      return GENDER_OPTIONS.includes(itemGender) ? itemGender : GENDER.BOTH;
    } catch (e) {
      console.error(e);
      return GENDER.BOTH;
    }
  }, [itemGender]);

  if (!open) return null;

  const handleClose = () => {
    try {
      onClose?.();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    try {
      if (itemType === ITEM_TYPES.HEADING && !String(itemTitle || "").trim()) {
        alert("Heading title is required");
        return;
      }
      if (itemType === ITEM_TYPES.NOTES && isEmptyHtml(itemHtml)) {
        alert("Notes content is required");
        return;
      }
      if (itemType === ITEM_TYPES.RICH_TEXT && !String(itemTitle || "").trim()) {
        alert("Other Text title is required");
        return;
      }
      // FREE_TEXT: content (HTML) is required; title/label is optional
      if (itemType === ITEM_TYPES.FREE_TEXT && isEmptyHtml(itemHtml)) {
        alert("Default Content is required for Free Text items");
        return;
      }

      await onSave?.();
    } catch (e) {
      console.error(e);
      alert("Failed to save item");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-medium text-gray-900">
                {editingItem ? "Edit Item" : "New Item"}
              </h2>
              <p className="text-xs text-gray-500 mt-1">Type: {typeLabel}</p>
            </div>

            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-200 rounded"
              type="button"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {itemType === ITEM_TYPES.HEADING && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Heading Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={itemTitle}
                  onChange={(e) => setItemTitle?.(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter heading..."
                  autoFocus
                />
              </div>

              <GenderSelect value={safeGender} onChange={setItemGender} />
            </div>
          )}

          {itemType === ITEM_TYPES.NOTES && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  <TinyMCEFullEditor
                    key={`notes-${editingItem?.id ?? "new"}`}
                    value={itemHtml || ""}
                    onChange={setItemHtml}
                    height={220}
                    placeholder="Enter notes..."
                  />
                </div>
              </div>

              <GenderSelect value={safeGender} onChange={setItemGender} />
            </div>
          )}

          {itemType === ITEM_TYPES.RICH_TEXT && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Title <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (shown as label; value is filled at result-entry time)
                  </span>
                </label>
                <input
                  type="text"
                  value={itemTitle || ""}
                  onChange={(e) => setItemTitle?.(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. Impression (Semen)"
                  autoFocus
                />
              </div>

              <GenderSelect value={safeGender} onChange={setItemGender} />
            </div>
          )}

          {/* FREE TEXT — rich editor; content editable by doctor at result time */}
          {itemType === ITEM_TYPES.FREE_TEXT && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Default Content <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs font-normal text-gray-400">(editable by doctor at result time)</span>
                </label>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  <TinyMCEFullEditor
                    key={`freetext-${editingItem?.id ?? "new"}`}
                    value={itemHtml || ""}
                    onChange={setItemHtml}
                    height={260}
                    placeholder="Enter default content..."
                    autoFocus
                  />
                </div>
              </div>

              <GenderSelect value={safeGender} onChange={setItemGender} />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Position
            </label>
            <input
              type="number"
              min="1"
              max={maxPosition}
              value={itemSortOrder}
              onChange={(e) => setItemSortOrder?.(e.target.value)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
          >
            <Save className="w-4 h-4" />
            {editingItem ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
